/**
 * Audit-log writer for Stock Solutions dose calculations (Enhancement C).
 *
 * Called outside React on each calculation. Appends to the legacy
 * localStorage trail (`reefapp.stockSolutions.history`, capped at 500)
 * AND to the `useServerBackedState` mirror for the `stock-history`
 * namespace so the offline fallback stays fresh, then best-effort
 * appends to the Pi (`/api/userdata/stock-history`, same cap) via a
 * read-modify-write. Writes-only, never blocks the UI; Pi failures are
 * logged to console but never thrown. Single-operator app, so the
 * naive concurrency of the read-modify-write is acceptable.
 */
import { mirrorKey } from '../hooks/useServerBackedState';
import type { StockSolutionLogEntry } from '../stock-solutions/historyTypes';
import { userDataApi } from './userdata-api';

/** Legacy browser-only trail — kept in sync, never deleted. */
const LEGACY_HISTORY_KEY = 'reefapp.stockSolutions.history';

/** `/api/userdata` namespace holding dose history on the Pi. */
export const STOCK_HISTORY_NAMESPACE = 'stock-history';

const MAX_ENTRIES = 500;

export interface StockSolutionLogMeta {
  slug: string;
  tankGallons: string;
  doseML: number;
  mode: 'correction' | 'daily' | 'ratio';
  currentLevel?: string;
  targetLevel?: string;
  units?: string;
  dailyConsumption?: string;
}

/** Append in place with the shared 500-entry cap (drops oldest). */
function appendCapped(arr: unknown[], entry: StockSolutionLogEntry): void {
  arr.push(entry);
  if (arr.length > MAX_ENTRIES) {
    arr.splice(0, arr.length - MAX_ENTRIES);
  }
}

function readLocalArray(key: string): unknown[] {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Keep BOTH browser copies fresh: the legacy trail (the history hook's
 * seed default) and the server-backed mirror (its offline fallback).
 */
function appendToLocalHistory(entry: StockSolutionLogEntry): void {
  try {
    const legacy = readLocalArray(LEGACY_HISTORY_KEY);
    appendCapped(legacy, entry);
    window.localStorage.setItem(LEGACY_HISTORY_KEY, JSON.stringify(legacy));

    const mirror = readLocalArray(mirrorKey(STOCK_HISTORY_NAMESPACE));
    appendCapped(mirror, entry);
    window.localStorage.setItem(
      mirrorKey(STOCK_HISTORY_NAMESPACE),
      JSON.stringify(mirror),
    );
  } catch {
    // storage full or disabled — non-fatal
  }
}

/** Best-effort read-modify-write append to the Pi copy. */
async function appendToServerHistory(
  entry: StockSolutionLogEntry,
): Promise<void> {
  const res = await userDataApi.get(STOCK_HISTORY_NAMESPACE);
  if (!res.ok) {
    console.info('[stock-solutions-log] Pi unreachable, kept locally:', res.error);
    return;
  }
  const arr: unknown[] = Array.isArray(res.value.data)
    ? [...res.value.data]
    : [];
  appendCapped(arr, entry);
  const put = await userDataApi.put(STOCK_HISTORY_NAMESPACE, arr);
  if (!put.ok) {
    console.info('[stock-solutions-log] non-fatal Pi write error:', put.error);
  }
}

export function writeStockSolutionLog(meta: StockSolutionLogMeta): void {
  const entry: StockSolutionLogEntry = {
    ts: new Date().toISOString(),
    slug: meta.slug,
    mode: meta.mode,
    tankGallons: meta.tankGallons,
    doseML: meta.doseML,
    currentLevel: meta.currentLevel,
    targetLevel: meta.targetLevel,
    units: meta.units,
    dailyConsumption: meta.dailyConsumption,
  };

  // Mirror to localStorage for CSV export / offline fallback.
  appendToLocalHistory(entry);

  // Fire-and-forget Pi append — best-effort, never throws.
  void appendToServerHistory(entry);
}
